import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Form,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";
import { apiAusenciasService } from "services/apiAusenciasService";
import { apiPermisosAusenciaService } from "services/apiPermisosAusenciaService";
import { apiMotivoAusenciasService } from "services/apiMotivoAusenciasService";
import { ApiSolicitudVacacionesService } from "services/ApiSolicitudVacacionesService";
import { useAuth } from "context/AuthProvider";
import { showAppAlert } from "components/common/AppAlerts";

const tipoAusenciaOptions = ["VACACIONES", "BAJA", "AUSENCIA"];
const tipoAusenciaBadgeColor = {
  VACACIONES: "success",
  AUSENCIA: "info",
  BAJA: "warning",
};
const mesNumerico = {
  ENERO: "01",
  FEBRERO: "02",
  MARZO: "03",
  ABRIL: "04",
  MAYO: "05",
  JUNIO: "06",
  JULIO: "07",
  AGOSTO: "08",
  SEPTIEMBRE: "09",
  OCTUBRE: "10",
  NOVIEMBRE: "11",
  DICIEMBRE: "12",
};

const formatTwoDigits = (number) => (number < 10 ? `0${number}` : `${number}`);
const addDaysToISODate = (isoDate, daysToAdd) => {
  if (!isoDate || !Number.isInteger(daysToAdd)) return "";
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + daysToAdd);
  return `${date.getFullYear()}-${formatTwoDigits(date.getMonth() + 1)}-${formatTwoDigits(date.getDate())}`;
};
const CalendarioAltaAusencia = ({
  tipo,
  dia,
  mes,
  anno,
  descripcion_id,
  descripcion,
  idAusencia,
  incidencias = [],
  empleadoId,
  onSaved,
}) => {
  const { empresa } = useAuth();
  const formattedDia = formatTwoDigits(dia);
  const formattedMes = mesNumerico[mes];
  const initialDate = `${anno}-${formattedMes}-${formattedDia}`;

  const [motivos, setMotivos] = useState([]);
  const [isSavingAusencia, setIsSavingAusencia] = useState(false);
  const [isSavingIncidencia, setIsSavingIncidencia] = useState(false);
  const [isCalculatingFechaHasta, setIsCalculatingFechaHasta] = useState(false);
  const [isDeletingAusencia, setIsDeletingAusencia] = useState(false);
  const [deletingIncidenciaId, setDeletingIncidenciaId] = useState(null);
  const [showIncidenciaForm, setShowIncidenciaForm] = useState(false);

  const [tipoAusencia, setTipoAusencia] = useState(tipo || "");
  const [descripcionId, setDescripcionId] = useState(descripcion_id || "");
  const [comentarioDetalle, setComentarioDetalle] = useState("");
  const [ausenciaRango, setAusenciaRango] = useState(false);
  const [ausenciaFechaDesde, setAusenciaFechaDesde] = useState(initialDate);
  const [ausenciaFechaHasta, setAusenciaFechaHasta] = useState(initialDate);

  const [incidenciaRango, setIncidenciaRango] = useState(false);
  const [incidenciaFechaDesde, setIncidenciaFechaDesde] = useState(initialDate);
  const [incidenciaFechaHasta, setIncidenciaFechaHasta] = useState(initialDate);
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [observacionIncidencia, setObservacionIncidencia] = useState("");

  useEffect(() => {
    const fetchMotivos = async () => {
      try {
        const response = await apiMotivoAusenciasService.getAllByID(empresa.id);
        const data = await response.json();
        setMotivos(data);
      } catch (error) {
        showAppAlert("Error al obtener los motivos de ausencia.", "error");
      }
    };

    fetchMotivos();
  }, [empresa.id]);

  useEffect(() => {
    setAusenciaRango(false);
    setAusenciaFechaDesde(initialDate);
    setAusenciaFechaHasta(initialDate);
    setIncidenciaRango(false);
    setIncidenciaFechaDesde(initialDate);
    setIncidenciaFechaHasta(initialDate);
    setShowIncidenciaForm(false);
    setTipoAusencia(tipo || "");
    setDescripcionId(descripcion_id || "");
    setComentarioDetalle("");
    setHoraInicio("");
    setHoraFin("");
    setObservacionIncidencia("");
  }, [initialDate, tipo, descripcion_id, incidencias]);

  const selectedMotivo = useMemo(
    () => motivos.find((motivo) => String(motivo.id) === String(descripcionId)),
    [motivos, descripcionId]
  );
  const diasPermitidosMotivo = selectedMotivo?.diasPermitidos || null;
  const tipoComputoDiasMotivo = selectedMotivo?.tipoComputoDias || "HABILES";
  const tipoComputoLabel = tipoComputoDiasMotivo === "NATURALES" ? "naturales" : "habiles";
  const hasDiasPermitidos = Number.isInteger(diasPermitidosMotivo) && diasPermitidosMotivo > 0;
  const usaDiasPermitidos = tipoAusencia === "AUSENCIA" && hasDiasPermitidos;

  useEffect(() => {
    if (tipoAusencia !== "AUSENCIA") {
      setAusenciaRango(false);
      setAusenciaFechaHasta(ausenciaFechaDesde);
    }
  }, [tipoAusencia, ausenciaFechaDesde]);

  useEffect(() => {
    if (usaDiasPermitidos) {
      setAusenciaRango(true);
    }
  }, [usaDiasPermitidos]);

  useEffect(() => {
    if (!usaDiasPermitidos) {
      setIsCalculatingFechaHasta(false);
      if (!ausenciaRango) {
        setAusenciaFechaHasta(ausenciaFechaDesde);
      }
      return;
    }

    if (!ausenciaFechaDesde || !descripcionId || !empleadoId) {
      setIsCalculatingFechaHasta(false);
      setAusenciaFechaHasta("");
      return;
    }

    if (tipoComputoDiasMotivo === "NATURALES") {
      setIsCalculatingFechaHasta(false);
      setAusenciaFechaHasta(addDaysToISODate(ausenciaFechaDesde, diasPermitidosMotivo));
      return;
    }

    let cancelled = false;

    const calcularFechaHastaHabiles = async () => {
      try {
        setIsCalculatingFechaHasta(true);
        const payload = {
          solicitud: {
            fechaSolicitud: new Date().toISOString().slice(0, 10),
          },
          ausencia: {
            empleadoId: Number(empleadoId),
            fechaDesde: ausenciaFechaDesde,
            fechaHasta: null,
            descripcion_id: Number(descripcionId),
            descripcion: selectedMotivo?.descripcion || "",
            tipo: "AUSENCIA",
            comentario: "",
          },
        };

        const calculo = await ApiSolicitudVacacionesService.calcular(empresa.id, payload);
        const fechaHastaCalculada = calculo?.ausencia?.fechaHasta || "";
        if (!cancelled) {
          setAusenciaFechaHasta(fechaHastaCalculada);
        }
      } catch (error) {
        if (!cancelled) {
          setAusenciaFechaHasta("");
          showAppAlert("No se pudo calcular la fecha fin para días hábiles.", "warning");
        }
      } finally {
        if (!cancelled) {
          setIsCalculatingFechaHasta(false);
        }
      }
    };

    calcularFechaHastaHabiles();

    return () => {
      cancelled = true;
    };
  }, [
    usaDiasPermitidos,
    tipoComputoDiasMotivo,
    ausenciaFechaDesde,
    descripcionId,
    empleadoId,
    empresa.id,
    ausenciaRango,
    diasPermitidosMotivo,
    selectedMotivo?.descripcion,
  ]);

  const handleDeleteAusencia = async (e) => {
    e.preventDefault();
    if (!idAusencia) return;

    try {
      setIsDeletingAusencia(true);
      const response = await apiAusenciasService.delete(idAusencia);
      if (!response.ok) {
        throw new Error("No se ha podido eliminar la ausencia.");
      }
      showAppAlert("Ausencia eliminada correctamente.", "success");
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      showAppAlert(error?.message || "No se ha podido eliminar el registro.", "error");
    } finally {
      setIsDeletingAusencia(false);
    }
  };

  const handleDeleteIncidencia = async (incidenciaId) => {
    if (!incidenciaId) return;

    try {
      setDeletingIncidenciaId(incidenciaId);
      await apiPermisosAusenciaService.deleteByID(incidenciaId);
      showAppAlert("Permiso horario eliminado correctamente.", "success");
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      showAppAlert(error?.message || "No se ha podido eliminar el permiso horario.", "error");
    } finally {
      setDeletingIncidenciaId(null);
    }
  };

  const handleGuardarAusencia = async () => {
    if (!tipoAusencia) {
      showAppAlert("Debe seleccionar un tipo de ausencia.", "warning");
      return;
    }

    if (tipoAusencia === "AUSENCIA" && !descripcionId) {
      showAppAlert("Debe seleccionar un motivo para AUSENCIA.", "warning");
      return;
    }

    if (usaDiasPermitidos && !ausenciaFechaDesde) {
      showAppAlert("Debe indicar la fecha de inicio.", "warning");
      return;
    }

    if (usaDiasPermitidos && !ausenciaFechaHasta) {
      showAppAlert("No se pudo calcular una fecha fin válida.", "warning");
      return;
    }

    if (!usaDiasPermitidos && ausenciaRango && ausenciaFechaHasta < ausenciaFechaDesde) {
      showAppAlert("La fecha hasta no puede ser anterior a la fecha desde.", "warning");
      return;
    }

    if (usaDiasPermitidos) {
      await apiAusenciasService.createDesdeHasta(
        tipoAusencia,
        Number(empleadoId),
        Number(descripcionId),
        ausenciaFechaDesde,
        ausenciaFechaHasta
      );
      return;
    }

    if (ausenciaRango) {
      await apiAusenciasService.createDesdeHasta(
        tipoAusencia,
        Number(empleadoId),
        tipoAusencia === "AUSENCIA" ? Number(descripcionId) : null,
        ausenciaFechaDesde,
        ausenciaFechaHasta
      );
      return;
    }

    const ausenciaData = {
      id: idAusencia,
      anno,
      mes,
      dia,
      descripcion_id: tipoAusencia === "AUSENCIA" ? Number(descripcionId) : null,
      descripcion: selectedMotivo?.descripcion || descripcion || "",
      tipo: tipoAusencia,
      empleado_id: Number(empleadoId),
      comentario: comentarioDetalle,
    };

    const response = await apiAusenciasService.create(ausenciaData);
    if (!response.ok) {
      throw new Error("No se pudo guardar la ausencia.");
    }
  };

  const handleGuardarIncidencia = async () => {
    if (!horaInicio || !horaFin) {
      showAppAlert("Debe indicar hora de inicio y hora de fin.", "warning");
      return;
    }
    if (horaInicio >= horaFin) {
      showAppAlert("La hora de fin debe ser posterior a la hora de inicio.", "warning");
      return;
    }
    if (incidenciaRango && incidenciaFechaHasta < incidenciaFechaDesde) {
      showAppAlert("La fecha hasta no puede ser anterior a la fecha desde.", "warning");
      return;
    }

    await apiPermisosAusenciaService.save({
      fechaInicio: incidenciaFechaDesde,
      fechaFin: incidenciaRango ? incidenciaFechaHasta : incidenciaFechaDesde,
      horaInicio,
      horaFin,
      idEmpleado: Number(empleadoId),
      observacion: observacionIncidencia,
    });
  };

  const handleGuardarAusenciaClick = async (e) => {
    e.preventDefault();
    try {
      setIsSavingAusencia(true);
      await handleGuardarAusencia();
      showAppAlert("Ausencia guardada.", "success");
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      showAppAlert(error?.message || "Hubo un error al guardar la ausencia.", "error");
    } finally {
      setIsSavingAusencia(false);
    }
  };

  const handleGuardarIncidenciaClick = async (e) => {
    e.preventDefault();
    try {
      setIsSavingIncidencia(true);
      await handleGuardarIncidencia();
      showAppAlert("Permiso horario guardado.", "success");
      setHoraInicio("");
      setHoraFin("");
      setObservacionIncidencia("");
      setShowIncidenciaForm(false);
      setIncidenciaRango(false);
      setIncidenciaFechaDesde(initialDate);
      setIncidenciaFechaHasta(initialDate);
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      showAppAlert(error?.message || "Hubo un error al guardar el permiso horario.", "error");
    } finally {
      setIsSavingIncidencia(false);
    }
  };

  return (
    <Form>
      <Card className="shadow-sm border-0 mb-3">
        <CardHeader className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <strong>Ausencia del día</strong>
            {idAusencia ? (
              <Badge color={tipoAusenciaBadgeColor[tipo] || "secondary"}>{tipo || "AUSENCIA"}</Badge>
            ) : (
              <Badge color="secondary">Sin ausencia</Badge>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>Tipo de ausencia</Label>
            <Input
              type="select"
              value={tipoAusencia}
              onChange={(e) => setTipoAusencia(e.target.value)}
              className="mb-2"
            >
              <option value="">Seleccione el tipo</option>
              {tipoAusenciaOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>

            {tipoAusencia === "AUSENCIA" ? (
              <>
                <Label for="motivoSelect">Motivo de ausencia</Label>
                <Input
                  type="select"
                  id="motivoSelect"
                  value={descripcionId}
                  onChange={(e) => setDescripcionId(e.target.value)}
                >
                  <option value="">Seleccione un motivo</option>
                  {motivos.map((motivo) => (
                    <option key={motivo.id} value={motivo.id}>
                      {motivo.descripcion}
                      {Number.isInteger(motivo.diasPermitidos) && motivo.diasPermitidos > 0
                        ? ` (${motivo.diasPermitidos} días ${motivo.tipoComputoDias === "NATURALES" ? "naturales" : "habiles"})`
                        : ""}
                    </option>
                  ))}
                </Input>
                {selectedMotivo ? (
                  <div className="mt-2">
                    
                    <Badge color={tipoComputoDiasMotivo === "NATURALES" ? "dark" : "primary"}>
                      Computo: {tipoComputoLabel}
                    </Badge>
                  </div>
                ) : null}
              </>
            ) : null}

            <Label className="mt-2">Observaciones</Label>
            <Input
              type="text"
              value={comentarioDetalle}
              onChange={(e) => setComentarioDetalle(e.target.value)}
              placeholder="Detalle opcional"
            />
          </FormGroup>

          <FormGroup check className="mb-3">
            <Label check>
              <Input
                type="checkbox"
                checked={ausenciaRango}
                disabled={usaDiasPermitidos}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAusenciaRango(checked);
                  if (!checked) {
                    setAusenciaFechaHasta(ausenciaFechaDesde);
                  }
                }}
              />
              Aplicar a un rango de días
            </Label>
          </FormGroup>

          {ausenciaRango ? (
            <FormGroup>
              <Label>Desde</Label>
              <Input
                type="date"
                value={ausenciaFechaDesde}
                onChange={(e) => setAusenciaFechaDesde(e.target.value)}
                className="mb-2"
              />
              <Label>Hasta</Label>
              <Input
                type="date"
                value={ausenciaFechaHasta || ""}
                onChange={(e) => setAusenciaFechaHasta(e.target.value)}
              />
              {usaDiasPermitidos ? (
                <small className="text-muted d-block mt-2">
                  {tipoComputoDiasMotivo === "NATURALES"
                    ? "Fecha fin calculada automaticamente en base a dias naturales."
                    : isCalculatingFechaHasta
                      ? "Calculando fecha fin..."
                      : "Fecha fin calculada automaticamente en base a dias habiles y festivos."}
                </small>
              ) : null}
            </FormGroup>
          ) : null}

          <div className="d-flex justify-content-end">
            {idAusencia ? (
              <Button
                className="btn btn-danger mr-2"
                disabled={isDeletingAusencia}
                onClick={handleDeleteAusencia}
                type="button"
              >
                {isDeletingAusencia ? "Eliminando..." : "Eliminar ausencia"}
              </Button>
            ) : null}
            <Button className="btn btn-success" type="button" disabled={isSavingAusencia} onClick={handleGuardarAusenciaClick}>
              {isSavingAusencia ? "Guardando..." : "Guardar ausencia"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-sm border-0">
        <CardHeader className="bg-light d-flex justify-content-between align-items-center">
          <div>
            <strong>Permisos horarios</strong>
            <Badge color="primary" pill className="ml-2">
              {incidencias.length}
            </Badge>
          </div>
          <Button color={showIncidenciaForm ? "secondary" : "primary"} size="sm" type="button" onClick={() => setShowIncidenciaForm((prev) => !prev)}>
            {showIncidenciaForm ? "Cancelar" : "Dar de alta permiso horario"}
          </Button>
        </CardHeader>
        <CardBody>
          <Collapse isOpen={showIncidenciaForm}>
            <FormGroup>
              <Label>Hora inicio</Label>
              <Input
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                className="mb-2"
              />
              <Label>Hora fin</Label>
              <Input type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
              <Label className="mt-2">Observacion</Label>
              <Input
                type="textarea"
                rows="2"
                value={observacionIncidencia}
                onChange={(e) => setObservacionIncidencia(e.target.value)}
                placeholder="Detalle opcional del permiso horario"
              />
            </FormGroup>

            <FormGroup check className="mb-3">
              <Label check>
                <Input
                  type="checkbox"
                  checked={incidenciaRango}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIncidenciaRango(checked);
                    if (!checked) {
                      setIncidenciaFechaHasta(incidenciaFechaDesde);
                    }
                  }}
                />
                Aplicar a un rango de días
              </Label>
            </FormGroup>

            {incidenciaRango ? (
              <FormGroup>
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={incidenciaFechaDesde}
                  onChange={(e) => setIncidenciaFechaDesde(e.target.value)}
                  className="mb-2"
                />
                <Label>Hasta</Label>
                <Input
                  type="date"
                  value={incidenciaFechaHasta}
                  onChange={(e) => setIncidenciaFechaHasta(e.target.value)}
                />
              </FormGroup>
            ) : null}

            <div className="text-right mb-3">
              <Button
                className="btn btn-success"
                type="button"
                disabled={isSavingIncidencia}
                onClick={handleGuardarIncidenciaClick}
              >
                {isSavingIncidencia ? "Guardando..." : "Guardar permiso horario"}
              </Button>
            </div>
          </Collapse>

          {incidencias.length > 0 ? (
            <Table size="sm" responsive className="mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Observacion</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {incidencias.map((inc) => (
                  <tr key={inc.id}>
                    <td>
                      <Badge color="light" className="text-dark">
                        {inc.inicio?.substring(0, 5)}
                      </Badge>
                    </td>
                    <td>
                      <Badge color="light" className="text-dark">
                        {inc.fin?.substring(0, 5)}
                      </Badge>
                    </td>
                    <td className="text-muted">{inc.observacion || "-"}</td>
                    <td className="text-right">
                      <Button
                        color="danger"
                        size="sm"
                        type="button"
                        disabled={deletingIncidenciaId === inc.id}
                        onClick={() => handleDeleteIncidencia(inc.id)}
                      >
                        {deletingIncidenciaId === inc.id ? "..." : "Eliminar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted">No hay permisos horarios registrados para este día.</div>
          )}
        </CardBody>
      </Card>
    </Form>
  );
};

export default CalendarioAltaAusencia;
